import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layout, LayoutContent, LayoutPanel } from "@astryxdesign/core/Layout";
import { useResizable, ResizeHandle } from "@astryxdesign/core/Resizable";
import { BottomSheet } from "@astryxdesign/core/BottomSheet";
import { Grid } from "@astryxdesign/core/Grid";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/HStack";
import { Section } from "@astryxdesign/core/Section";
import { Toolbar } from "@astryxdesign/core/Toolbar";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { Badge } from "@astryxdesign/core/Badge";
import { Card } from "@astryxdesign/core/Card";
import { AspectRatio } from "@astryxdesign/core/AspectRatio";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Banner } from "@astryxdesign/core/Banner";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Button } from "@astryxdesign/core/Button";
import { IconButton } from "@astryxdesign/core/IconButton";
import { SegmentedControl, SegmentedControlItem } from "@astryxdesign/core/SegmentedControl";
import { useHotkeys } from "@astryxdesign/core/hooks";
import { useToast } from "@astryxdesign/core/Toast";
import { LayoutGrid, Maximize2, Minimize2, PanelLeftClose, PanelLeftOpen, Video, VideoOff } from "lucide-react";
import { api, hasPermission, type Camera } from "../../lib/api";
import { VideoTile } from "../stream/VideoTile";
import { createStreamSlots, getMaxConcurrentStreams } from "../stream/streamSlots";
import { createTileCommands } from "../stream/tileCommands";
import { getAutoStartStreams } from "../stream/autoStart";
import { useWallMode } from "../stream/useWallMode";
import { type GridColumns, useGridColumns } from "../../lib/useGridColumns";
import { useClockTick, formatSiteTime } from "../../lib/useClockTick";
import { RetryBanner } from "../../components/RetryBanner";
import { CameraPickerPanel } from "./CameraPickerPanel";
import { useLiveSelection } from "./useLiveSelection";
import { FocusDialog } from "./FocusDialog";
import { StreamLimitsDialog } from "./StreamLimitsDialog";
import { RunningStreamsDialog } from "./RunningStreamsDialog";
import { PendingRequestsDialog } from "./PendingRequestsDialog";
import { useAutoGridColumns } from "./useAutoGridColumns";
import type { HealthFeed } from "../health/useHealthEvents";
import { useIsTabletViewport } from "../../lib/responsive";
import { isServerLive } from "../stream/serverHealth";
import { useAuth } from "../auth/AuthProvider";

// Matches the two `Grid gap={GRID_GAP_STEP}` call sites below — hand-
// maintained, not derived, because the solver needs a plain pixel number and
// Grid's gap is a themed step. If one changes, the other must too, or the
// auto-column solver starts packing against a gap the grid isn't using.
// Step 1 == 4px normally; wall mode drops both to 0 for edge-to-edge tiles —
// a wall-mounted screen should show video and nothing else, gaps included.
const GRID_GAP_STEP = 1;
const GRID_GAP_PX = 4;

// Wide enough to hold the 28px expand button plus its focus ring.
const COLLAPSED_PANEL_PX = 44;

// Two 16:9 tiles side by side at 375px are ~170px wide — small, but you can
// still tell what is happening in one. Three cannot.
const MAX_NARROW_GRID_COLUMNS = 2;

interface LiveGridPageProps {
  health: HealthFeed;
  /** Lets the empty states hand the user off to the management page.
   *  Undefined for a viewer without cameras.manage — that role has no
   *  Cameras tab to send them to, so the empty state shows no action. */
  onManageCameras?: () => void;
}

// Loading placeholder count is a guess (the real camera count isn't known
// until the query resolves) — three matches the grid's own default column
// count so the skeleton doesn't visibly reflow into the real layout.
const LOADING_TILE_COUNT = 3;

export function LiveGridPage({ health, onManageCameras }: LiveGridPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectCode } = useParams<{ projectCode?: string }>();
  // Declared early: the notice subscription below (for a declined request)
  // and wall-mode's own error effect further down both need it.
  const showToast = useToast();

  const {
    data: cameras,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ["cameras"],
    queryFn: api.cameras.list,
    // The grid is a live view: poll for camera-list changes (new/removed
    // cameras from the management page) without requiring a manual refresh.
    refetchInterval: 15_000,
  });
  // Powers the camera picker tree — a scoped viewer who can watch a
  // project's cameras but lacks projects.manage still gets this (the list
  // route has no permission gate beyond a valid session), just with no
  // folders to browse if it somehow came back empty.
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: api.projects.list });
  const { data: devices } = useQuery({ queryKey: ["devices"], queryFn: api.devices.list });

  // The project the URL names lives on to seed the picker's initial folder
  // (see selectProject below) and the toolbar's site-local-time clock — it
  // is no longer a hard scope. The picker tree spans every project the
  // viewer can see, and ticking is what puts a camera on the canvas
  // regardless of which folder it lives in.
  const selectedProject = useMemo(() => {
    if (!projectCode) return null;
    return (projects ?? []).find((p) => p.code.toLowerCase() === projectCode.toLowerCase()) ?? null;
  }, [projects, projectCode]);
  // Only a real "not found" once projects have actually loaded — otherwise
  // a valid code would flash this warning for one frame while `projects` is
  // still undefined.
  const projectCodeNotFound = projectCode !== undefined && projects !== undefined && selectedProject === null;

  // Preserves the current `?cams=` selection across a folder switch — a
  // plain `navigate(path)` replaces the whole URL, search string included,
  // which would otherwise silently drop the wall's own shareable link every
  // time the operator opened a different site's folder.
  const selectProject = (project: { code: string } | null) => {
    navigate({ pathname: project ? `/live/${encodeURIComponent(project.code)}` : "/live", search: location.search });
  };

  // One slot budget for the whole grid's lifetime, not recreated per render —
  // recreating it would reset the count out from under tiles already holding
  // a slot. Also shared with the focus dialog: see FocusDialog's own comment
  // on why FOCUS_TILE_ID must draw from this same store, not a separate one.
  const slotsRef = useRef(createStreamSlots(getMaxConcurrentStreams()));
  const activeSlots = useSyncExternalStore(slotsRef.current.subscribe, slotsRef.current.active);
  // The picker's own tick cap — same number as the slot budget above, since
  // "on the wall" and "holding a stream slot" are meant to be the same
  // thing for every camera an operator explicitly ticked. Read once, same
  // reasoning as slotsRef: it must not change out from under an operator
  // mid-session just because the deploy-time env var could in theory differ
  // from what the server enforces (see the plan's own note on this gap).
  const limit = slotsRef.current.limit;

  // How many cameras a fresh browser lights up on its own before the
  // operator has ticked anything — see seedSelection's own comment in
  // liveSelection.ts for why zero is a legitimate setting, not a bug.
  const autoStartLimit = useRef(getAutoStartStreams()).current;

  const { selection, isReady: isSelectionReady, toggle } = useLiveSelection(cameras, limit, autoStartLimit);
  // Refs so the long-lived notice subscription below (registered once, see
  // its own effect) reads the CURRENT selection/toggle when a notice
  // arrives, not whatever they were when the effect last ran — same pattern
  // as myRequestsRef and actionsRef elsewhere in this feature.
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const toggleRef = useRef(toggle);
  toggleRef.current = toggle;

  const [gridColumns, setGridColumns] = useGridColumns();
  const [focusedCameraId, setFocusedCameraId] = useState<string | null>(null);
  const [isLimitsOpen, setIsLimitsOpen] = useState(false);
  const [isRunningOpen, setIsRunningOpen] = useState(false);
  const [isRequestsOpen, setIsRequestsOpen] = useState(false);
  const [isPickerSheetOpen, setIsPickerSheetOpen] = useState(false);

  // Cameras the operator has deliberately stopped.
  //
  // Without this, stopping a tile would be undone the moment somebody else
  // started the same camera: it would become "already running", and this
  // tile would auto-start right back — a ticked camera means "keep this on
  // the wall", not "always be playing it". An explicit stop is a decision,
  // and it outranks that until the operator presses play (or re-ticks it)
  // again.
  const [suppressed, setSuppressed] = useState<ReadonlySet<string>>(() => new Set());

  // Requests this browser made, by camera. Kept so the tile can say it is
  // waiting, and so an answer arriving on the shared notice channel can be
  // recognised as the reply to OUR question rather than somebody else's.
  const [myRequests, setMyRequests] = useState<ReadonlyMap<string, string>>(() => new Map());
  // Mirrored in a ref because the notice subscription below is registered
  // once, in an effect that must read myRequests as it stands WHEN A NOTICE
  // ARRIVES — not as it stood when the effect last ran. Same pattern as
  // useHlsStream's own refs for values a long-lived callback needs current.
  const myRequestsRef = useRef(myRequests);
  myRequestsRef.current = myRequests;

  const suppressAutoStart = useCallback((cameraId: string) => {
    setSuppressed((prev) => new Set(prev).add(cameraId));
  }, []);
  const allowAutoStart = useCallback((cameraId: string) => {
    setSuppressed((prev) => {
      if (!prev.has(cameraId)) return prev;
      const next = new Set(prev);
      next.delete(cameraId);
      return next;
    });
  }, []);

  // Wraps the raw tick/untick from useLiveSelection with the bookkeeping a
  // bare toggle would otherwise leave stale:
  //  - Ticking clears a stale suppression, or a camera evicted earlier this
  //    session would silently stay dark after being re-added to the wall.
  //  - Unticking drops any outstanding slot-request tracking for it — once
  //    the tile unmounts nothing is watching for the answer, and leaving the
  //    entry behind would leak it in myRequests forever if one never comes.
  const handleToggleCamera = useCallback(
    (cameraId: string) => {
      if (selection.includes(cameraId)) {
        setMyRequests((prev) => {
          if (!prev.has(cameraId)) return prev;
          const next = new Map(prev);
          next.delete(cameraId);
          return next;
        });
      } else {
        allowAutoStart(cameraId);
      }
      toggle(cameraId);
    },
    [selection, allowAutoStart, toggle],
  );

  // Deliberately no try/catch: the caller is StreamContentionDialog, which
  // awaits this and puts a failure in a Banner beside the button the
  // operator just pressed — the only escalation a viewer has past a full
  // wall, with them looking straight at it. Swallowing the error here would
  // leave the one surface that CAN report it believing the request is
  // pending, which is exactly the state `myRequests` below is reserved for
  // encoding (see setMyRequests, only ever called on success).
  const requestSlot = useCallback(
    async (cameraId: string, evictCameraId: string) => {
      const created = await api.streamRequests.create({
        camera_id: cameraId,
        evict_camera_id: evictCameraId,
      });
      setMyRequests((prev) => new Map(prev).set(cameraId, created.id));
    },
    [],
  );

  // One command channel for the whole grid's lifetime, not recreated per
  // render — same reasoning as slotsRef above. Shared with the focus dialog
  // (passed down alongside slots) so a camera evicted while open there
  // shuts down cleanly too, rather than only on the grid tile behind it.
  const commandsRef = useRef(createTileCommands());

  // Replaces two effects that used to read `health.lastEviction`/
  // `health.lastAnswer` — values parked in health's own state and clobbered
  // by the next health frame roughly every two seconds (see useHealthEvents'
  // own comment on why that shape was replaced with this subscription).
  useEffect(
    () =>
      health.subscribeNotice((notice) => {
        const stopsAStream =
          notice.type === "stream_evicted" || (notice.type === "eviction_answered" && notice.approved);

        if (stopsAStream) {
          // Somebody else's deliberate decision outranks the auto-start rule,
          // exactly as an operator's own Stop does (see `suppressed`'s own
          // comment above) — without this, the camera list's 15s refetch
          // re-running the auto-start rule would race every other browser
          // on the wall for the slot that was just freed, and the winner
          // would bounce whoever lost straight back into a full-wall
          // refusal.
          suppressAutoStart(notice.camera_id);
          // Somebody else stopping this camera's stream makes its segments
          // simply stop existing, which hls.js would otherwise report as a
          // decode failure a few seconds later — presenting a deliberate
          // decision as a fault. Shutting the tile down cleanly the moment
          // the notice arrives avoids that; the toast (raised elsewhere,
          // addressed to the person rather than a tile) explains why.
          commandsRef.current.send(notice.camera_id, "stop");
        }

        if (notice.type !== "eviction_answered") return;
        // Only OUR request. Everyone receives every answer, because an
        // approval stops a stream that others may be watching.
        if (myRequestsRef.current.get(notice.started_camera_id) !== notice.request_id) return;

        setMyRequests((prev) => {
          const next = new Map(prev);
          next.delete(notice.started_camera_id);
          return next;
        });
        if (!notice.approved) {
          // Told explicitly, not left to notice the dialog quietly reverted
          // to "Ask for a slot" — a decline used to tell the requester
          // nothing at all. Default (info) tone, not "error": nobody did
          // anything wrong, the same reasoning App's own eviction toast
          // documents for the same kind of ordinary, negative outcome.
          showToast({ body: `Your request for ${notice.started_camera_name} was declined.` });
          return;
        }
        // The slot is admitted for this camera but nothing is pulling it yet
        // — the requester still needs their own lease.
        allowAutoStart(notice.started_camera_id);
        if (selectionRef.current.includes(notice.started_camera_id)) {
          // Already ticked (e.g. re-ticked before the answer arrived) — the
          // mounted tile just needs telling to start rather than pressing
          // play again after waiting for approval.
          commandsRef.current.send(notice.started_camera_id, "start");
        } else if (selectionRef.current.length >= limit) {
          // The picker's own cap is full. Silently dropping the approval
          // here would waste the slot the server just freed for this
          // request — tileCommands queues nothing for an unmounted tile
          // (see its own comment), so a send with nobody ticked reaches no
          // listener at all.
          showToast({
            body: `${notice.started_camera_name} was approved, but your wall is full — untick a camera, then tick it back on.`,
          });
        } else {
          // Not on the wall — ticking it mounts a fresh tile, which starts
          // on its own: autoStartFor only checks `suppressed`, cleared just
          // above.
          toggleRef.current(notice.started_camera_id);
        }
      }),
    [health.subscribeNotice, allowAutoStart, suppressAutoStart, showToast, limit],
  );

  const { user } = useAuth();
  const canManageCameras = hasPermission(user, "cameras.manage");

  // Polled only for people who can answer, and only to put a number on the
  // button — the queue itself is fetched when the dialog opens.
  const { data: pendingRequests } = useQuery({
    queryKey: ["stream-requests"],
    queryFn: api.streamRequests.list,
    enabled: canManageCameras,
    refetchInterval: 10_000,
  });
  const pendingRequestCount = pendingRequests?.length ?? 0;
  const wall = useWallMode();
  useHotkeys([{ keys: "mod+shift+f", onPress: () => wall.toggle() }]);
  // Fed to both the Grid (themed step) and the auto-column solver (raw
  // pixels) — see GRID_GAP_STEP/GRID_GAP_PX's own comment.
  const gridGapStep = wall.isActive ? 0 : GRID_GAP_STEP;
  const gridGapPx = wall.isActive ? 0 : GRID_GAP_PX;
  // wall.error is set synchronously inside enter()'s catch, but toasting
  // from inside a click handler would fire mid-render if enter() itself
  // dispatched the toast — surfacing it via an effect keyed on the error
  // value keeps this a plain, predictable side effect of state changing.
  useEffect(() => {
    if (wall.error) showToast({ body: wall.error, type: "error" });
  }, [wall.error, showToast]);

  const folderPanelResize = useResizable({
    defaultSize: 280,
    minSizePx: 220,
    maxSizePx: 480,
    collapsible: true,
    collapsedSize: 40,
    autoSaveId: "mtn-cctv-live-folder-panel",
  });
  // Below this width the camera picker is not a narrow panel — it is not a
  // panel at all. A resizable rail is built on dragging its edge, which a
  // touch screen cannot do, and collapsing it only trades the rail for a
  // 28px button. The same picker lives in a BottomSheet on this width
  // instead, opened from the "Cameras" button below.
  //
  // This replaced an effect that auto-collapsed the panel on entering narrow
  // width and had to remember whether the collapse was its own doing, so it
  // would not overwrite a viewer's saved desktop preference. Not rendering
  // the panel at all leaves the persisted desktop size untouched by
  // construction, so none of that bookkeeping is needed.
  const isNarrowViewport = useIsTabletViewport();

  // The ids on the wall, resolved to full Camera objects, in selection
  // order — a fresh tick lands in the next free window, as on Hikvision/
  // Uniview's own live-view grids (see the plan's own note on tile order).
  //
  // This ONE array feeds the grid map, FocusDialog's filmstrip, and the
  // focus-clear effect below. Deriving each independently is what used to
  // make focusing a slot-neutral operation by accident (the grid's own
  // enabled={false} always freed a slot before FocusDialog's tile acquired
  // one) — splitting them would let the focus tile become a net +1 slot the
  // moment the two lists could ever disagree.
  const camerasById = useMemo(() => new Map((cameras ?? []).map((c) => [c.id, c] as const)), [cameras]);
  const canvasCameras = useMemo(
    () => selection.map((id) => camerasById.get(id)).filter((c): c is Camera => c !== undefined),
    [selection, camerasById],
  );

  // A ticked camera always wants to be running — that is what ticking it
  // means — unless an explicit Stop (or somebody else's eviction) suppressed
  // it. There is no "top N" rule to apply here anymore: every camera on the
  // canvas got there because an operator asked for it.
  const autoStartFor = useCallback((cameraId: string) => !suppressed.has(cameraId), [suppressed]);

  // Same deliberate choice as App's own liveCount: keeps the last known
  // count through a health.phase of "interrupted" rather than collapsing to
  // 0, since byCamera itself isn't cleared on interruption.
  const streamingCount = canvasCameras.filter((c) => isServerLive(health.byCamera.get(c.id))).length;

  // If the focused camera is unticked, disabled, or deleted, the dialog must
  // not keep streaming a camera that's no longer on the wall — mirrors the
  // pre-existing selected-project-deleted guard below.
  useEffect(() => {
    if (focusedCameraId && !canvasCameras.some((c) => c.id === focusedCameraId)) {
      setFocusedCameraId(null);
    }
  }, [canvasCameras, focusedCameraId]);

  const focusedCamera = focusedCameraId ? (canvasCameras.find((c) => c.id === focusedCameraId) ?? null) : null;

  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const autoColumns = useAutoGridColumns(gridWrapperRef, canvasCameras.length, gridGapPx);
  // Clamped, not just hidden from the control above: the preference persists
  // across devices via localStorage, so a viewer who chose 5 columns at a
  // desk would otherwise open the same browser on a phone to five 60px
  // slivers and no control offering a way back.
  const gridColumnsValue = Math.min(
    gridColumns === "auto" ? autoColumns : gridColumns,
    isNarrowViewport ? MAX_NARROW_GRID_COLUMNS : Number.POSITIVE_INFINITY,
  );

  const now = useClockTick();

  const folderPanel = (
    <>
      {/* `collapsible` collapses the panel to width 0 — `collapsedSize` is a
          drag threshold, not a resting width. At 0 the expand button inside
          it was measured rendering at x=-6, i.e. clipped off the left edge of
          the viewport with no way to click it: a panel that could be closed
          and then never reopened. The width is pinned explicitly here rather
          than relying on the hook. */}
      <LayoutPanel
        width={folderPanelResize.isCollapsed ? COLLAPSED_PANEL_PX : folderPanelResize.size}
        hasDivider={false}
        padding={folderPanelResize.isCollapsed ? 0 : 3}
      >
        {folderPanelResize.isCollapsed ? (
          <VStack hAlign="center" padding={2}>
            <IconButton
              label="Expand camera picker"
              tooltip="Pick cameras"
              icon={<PanelLeftOpen size={16} />}
              variant="ghost"
              size="sm"
              onClick={() => folderPanelResize.expand()}
            />
          </VStack>
        ) : (
          // Not isScrollable here — CameraPickerPanel now owns its own
          // internal scroll region (its tree) so its docked Selected
          // section can stay pinned below it instead of scrolling away with
          // a long tree. Scrolling this whole VStack instead would defeat
          // that: the header row would scroll off along with everything
          // else, and Selected would go back to trailing the tree rather
          // than sitting fixed beneath it.
          <VStack gap={2} height="fill">
            <HStack justify="between" vAlign="center">
              <Text type="label" color="secondary">
                Pick cameras
              </Text>
              {/* Collapsing was previously only possible by dragging the
                  resize handle past a threshold — a gesture nothing on
                  screen advertises, so in practice the panel could not be
                  put away. This is the same action the collapsed rail's
                  expand button performs in reverse. */}
              <IconButton
                label="Collapse camera picker"
                tooltip="Collapse"
                icon={<PanelLeftClose size={16} />}
                variant="ghost"
                size="sm"
                onClick={() => folderPanelResize.collapse()}
              />
            </HStack>
            <CameraPickerPanel
              projects={projects ?? []}
              devices={devices ?? []}
              cameras={cameras ?? []}
              selection={selection}
              onToggle={handleToggleCamera}
              health={health}
              limit={limit}
              initialExpandedProjectId={selectedProject?.id ?? null}
            />
          </VStack>
        )}
      </LayoutPanel>
      <ResizeHandle direction="horizontal" hasDivider resizable={folderPanelResize.props} label="Resize camera picker" />
    </>
  );

  let body: ReactNode;
  const loadingSkeleton = (
    <Grid columns={gridColumnsValue} gap={gridGapStep}>
      {Array.from({ length: LOADING_TILE_COUNT }, (_, i) => (
        // eslint-disable-next-line react/no-array-index-key -- fixed-count static placeholders, never reordered
        // The real tile's header is now an Overlay drawn inside the 16:9
        // frame (VideoTile.tsx), not a bar stacked above it — so a plain
        // AspectRatio shimmer already matches its real footprint exactly.
        // A skeleton header row here would make loading tiles *taller*
        // than real ones, the opposite of the jump this once prevented.
        <Card key={i} padding={0} elevation="low" style={{ overflow: "hidden" }}>
          <AspectRatio ratio={16 / 9}>
            <Skeleton index={i} radius={0} />
          </AspectRatio>
        </Card>
      ))}
    </Grid>
  );

  if (isLoading || !isSelectionReady) {
    // !isSelectionReady covers the one extra render between the camera list
    // resolving and the first-run seed effect committing — without this, a
    // brand-new browser would flash "Nothing on the wall" for a frame before
    // its seeded cameras appear.
    body = loadingSkeleton;
  } else if (error) {
    body = (
      <RetryBanner
        title="Couldn't load cameras"
        description={error instanceof Error ? error.message : "Unknown error"}
        onRetry={() => void refetch()}
        isRetrying={isRefetching}
      />
    );
  } else if ((cameras ?? []).length === 0) {
    body = (
      <Section padding={8}>
        <EmptyState
          icon={<Video size={32} />}
          title="No cameras yet"
          description={
            onManageCameras
              ? "Add a camera from the Cameras tab to start monitoring."
              : "Ask an admin to add a camera to start monitoring."
          }
          actions={onManageCameras && <Button label="Go to Cameras" variant="primary" onClick={onManageCameras} />}
        />
      </Section>
    );
  } else if (canvasCameras.length === 0) {
    // The most common empty state now — an operator hasn't ticked anything
    // yet, or unticked everything. Not routed to "Go to Cameras": there is
    // nothing wrong with the fleet, nothing to fix on the management page —
    // the fix is one checkbox away, in the sidebar (or the sheet, on a
    // phone) right next to this message.
    body = (
      <Section padding={8}>
        <EmptyState
          icon={<VideoOff size={32} />}
          title="Nothing on the wall"
          description={
            isNarrowViewport
              ? "Tap Cameras below to tick one and start watching."
              : "Tick a camera in the sidebar to start watching."
          }
        />
      </Section>
    );
  } else {
    body = (
      <>
        {/* A wall-mounted screen should show video and nothing else — grid
            size, stream limits, and the wall-mode toggle itself all wait
            outside it. Escape (native Fullscreen API behavior) and
            mod+shift+f (the hotkey above, a plain keydown listener that
            doesn't depend on this toolbar existing) both still exit. */}
        {!wall.isActive && (
        <Toolbar
          label="Live wall controls"
          startContent={
            selectedProject ? (
              <HStack gap={1.5} vAlign="center">
                <Text type="supporting" color="secondary">
                  {selectedProject.name} local time
                </Text>
                <Text type="code" hasTabularNumbers>
                  {formatSiteTime(now, selectedProject.timezone)}
                </Text>
              </HStack>
            ) : undefined
          }
          endContent={
            <HStack gap={3} vAlign="center" wrap="wrap">
              {/* Four and five columns are offered only where they produce a
                  watchable tile. At 375px, five columns is a 60px-wide
                  camera — not a smaller view of the site, just nothing. */}
              <SegmentedControl
                label="Grid size"
                size="sm"
                value={String(gridColumns)}
                onChange={(v) => setGridColumns(v === "auto" ? "auto" : (Number(v) as GridColumns))}
              >
                <SegmentedControlItem value="auto" label="Auto" icon={<LayoutGrid size={14} />} isLabelHidden />
                <SegmentedControlItem value="2" label="2" />
                {!isNarrowViewport && <SegmentedControlItem value="3" label="3" />}
                {!isNarrowViewport && <SegmentedControlItem value="4" label="4" />}
                {!isNarrowViewport && <SegmentedControlItem value="5" label="5" />}
              </SegmentedControl>
              {/* The badge is a button because it is where somebody is looking
                  at the moment they want the number to be different. Viewers
                  can open it too — they can read the ceiling, which is what
                  explains a refusal to them, but not change it. */}
              <Button
                label={`${activeSlots}/${slotsRef.current.limit === Number.POSITIVE_INFINITY ? "∞" : slotsRef.current.limit} streams`}
                variant="ghost"
                size="sm"
                onClick={() => setIsLimitsOpen(true)}
              />
              {/* What is actually using the capacity. Without it, "no free
                  stream slots" is a wall an operator cannot see through. */}
              <Button
                label="Running"
                variant="ghost"
                size="sm"
                onClick={() => setIsRunningOpen(true)}
              />
              {/* Only for people who can actually answer. A queue shown to
                  somebody with no power over it is noise. */}
              {canManageCameras && (
                <Button
                  label={pendingRequestCount > 0 ? `Requests (${pendingRequestCount})` : "Requests"}
                  variant={pendingRequestCount > 0 ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setIsRequestsOpen(true)}
                />
              )}
              {health.phase === "interrupted" && <Badge variant="warning" label="Health feed reconnecting…" />}
              <IconButton
                label={wall.isActive ? "Exit wall mode" : "Enter wall mode"}
                tooltip={wall.isActive ? "Exit wall mode (Esc)" : "Wall mode (⌘⇧F)"}
                icon={wall.isActive ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                variant="ghost"
                size="sm"
                onClick={wall.toggle}
                isDisabled={!wall.isSupported}
              />
            </HStack>
          }
        />
        )}

        <VStack ref={gridWrapperRef}>
          <Grid columns={gridColumnsValue} gap={gridGapStep}>
            {canvasCameras.map((camera) => (
              <VideoTile
                key={camera.id}
                camera={camera}
                slots={slotsRef.current}
                enabled={focusedCameraId !== camera.id}
                autoStart={autoStartFor(camera.id)}
                health={health.byCamera.get(camera.id)}
                isFeedLive={health.phase === "live"}
                onOpenFocus={() => setFocusedCameraId(camera.id)}
                onRemoveFromWall={() => handleToggleCamera(camera.id)}
                onManualPlay={() => allowAutoStart(camera.id)}
                onManualStop={() => suppressAutoStart(camera.id)}
                canEvict={canManageCameras}
                commands={commandsRef.current}
                isSlotRequested={myRequests.has(camera.id)}
                onRequestSlot={(evictCameraId) => requestSlot(camera.id, evictCameraId)}
              />
            ))}
          </Grid>
        </VStack>
      </>
    );
  }

  return (
    <Layout
      ref={wall.containerRef}
      height="fill"
      // A wall-mounted screen should show video and nothing else — the
      // camera picker is a navigation aid for choosing what to watch, not
      // something to leave on screen once that choice is made.
      start={wall.isActive || isNarrowViewport ? undefined : folderPanel}
      content={
        <LayoutContent isScrollable>
          <Section padding={wall.isActive ? 0 : 4}>
            <VStack gap={wall.isActive ? 0 : 4}>
              {!wall.isActive && (
              <HStack justify="between" vAlign="center" wrap="wrap" gap={2}>
                <VStack gap={0.5}>
                  <Heading level={1}>Live cameras</Heading>
                  <Text type="supporting" color="secondary">
                    {streamingCount} of {canvasCameras.length} streaming now
                  </Text>
                </VStack>
              </HStack>
              )}

              {/* Stands in for the site tree, which is not rendered at this
                  width. Opens the same picker in a BottomSheet — without it
                  a phone viewer would be pinned to whichever selection is
                  already saved, with no way to change it once the wall isn't
                  empty. Rendered regardless of canvas state (loading, empty,
                  or a full wall) so it's always reachable, and hidden in
                  wall mode for the same reason the desktop panel is. */}
              {!wall.isActive && isNarrowViewport && (
                <>
                  <Button
                    label={`Cameras (${canvasCameras.length}/${limit})`}
                    variant="secondary"
                    icon={<Video size={16} />}
                    onClick={() => setIsPickerSheetOpen(true)}
                  />
                  <BottomSheet isOpen={isPickerSheetOpen} onOpenChange={setIsPickerSheetOpen} label="Pick cameras" height="tall">
                    <CameraPickerPanel
                      projects={projects ?? []}
                      devices={devices ?? []}
                      cameras={cameras ?? []}
                      selection={selection}
                      onToggle={handleToggleCamera}
                      health={health}
                      limit={limit}
                      initialExpandedProjectId={selectedProject?.id ?? null}
                    />
                  </BottomSheet>
                </>
              )}

              {!wall.isActive && projectCodeNotFound && (
                <Banner
                  status="warning"
                  title="Unknown site"
                  description={`No site matches the code "${projectCode}" — it may have been renamed or removed.`}
                  endContent={<Button label="Browse all sites" variant="secondary" size="sm" onClick={() => selectProject(null)} />}
                />
              )}

              {body}
            </VStack>
          </Section>

          <RunningStreamsDialog isOpen={isRunningOpen} onOpenChange={setIsRunningOpen} />

      <PendingRequestsDialog isOpen={isRequestsOpen} onOpenChange={setIsRequestsOpen} />

      <StreamLimitsDialog
        isOpen={isLimitsOpen}
        onOpenChange={setIsLimitsOpen}
        canEdit={canManageCameras}
      />

      <FocusDialog
            focusedCamera={focusedCamera}
            cameras={canvasCameras}
            onFocusChange={setFocusedCameraId}
            slots={slotsRef.current}
            commands={commandsRef.current}
            health={health}
          />
        </LayoutContent>
      }
    />
  );
}
